import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import styled from "styled-components";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import Slider from "common/Slider/Slider.tsx";
import { useEffect, useState } from "react";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import Text from "common/Typography/Text";
import { FormFieldTitle } from "features/bots/common/styles.ts";

interface HedgingRatioSelectorProps {
  longHedgingRatio: number | string | undefined;
  setLongHedgingRatio: (value: number) => void;
  isMultiValue?: boolean;
  longInputTestId?: string;
  shortInputTestId?: string;
  longLabel?: string;
  shortLabel?: string;
  useFormFieldTitles?: boolean;
}

const HedgingRatioSelector = ({
  longHedgingRatio,
  setLongHedgingRatio,
  isMultiValue,
  longInputTestId,
  shortInputTestId,
  longLabel,
  shortLabel,
  useFormFieldTitles,
}: HedgingRatioSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const parsedLongHedgingRatio =
    longHedgingRatio !== undefined
      ? parseFloat(longHedgingRatio.toString())
      : undefined;
  const [longInputValue, setLongInputValue] = useState<
    number | string | null | undefined
  >(parsedLongHedgingRatio);
  const shortHedgingRatio =
    parsedLongHedgingRatio !== undefined
      ? 100 - parsedLongHedgingRatio
      : undefined;
  const [shortInputValue, setShortInputValue] = useState<
    number | string | null | undefined
  >(shortHedgingRatio);

  useEffect(() => {
    if (
      parseFloat(longInputValue?.toString() ?? "") !== parsedLongHedgingRatio
    ) {
      setLongInputValue(parsedLongHedgingRatio);
    }
    if (parseFloat(shortInputValue?.toString() ?? "") !== shortHedgingRatio) {
      setShortInputValue(shortHedgingRatio);
    }
  }, [parsedLongHedgingRatio, shortHedgingRatio]);

  const getRatio = (value: number | number[] | string | null | undefined) => {
    const parsedValue = parseFloat(
      Array.isArray(value) ? value[0].toString() : value?.toString() ?? ""
    );
    if (Number.isNaN(parsedValue)) return 0;
    return Math.min(Math.max(parsedValue, 0), 100);
  };

  const updateLongHedging = (
    value: number | number[] | string | null | undefined
  ) => {
    setLongInputValue(Array.isArray(value) ? value[0] : value);
    const nextLongHedging = getRatio(value);
    setLongHedgingRatio(nextLongHedging);
  };
  const updateShortHedging = (
    value: number | number[] | string | null | undefined
  ) => {
    setShortInputValue(Array.isArray(value) ? value[0] : value);
    const nextShortHedging = getRatio(value);
    setLongHedgingRatio(100 - nextShortHedging);
  };
  const getTitle = (label: string) => {
    if (useFormFieldTitles) {
      return <FormFieldTitle>{label}</FormFieldTitle>;
    }
    return <Text type="secondary">{label}</Text>;
  };

  return (
    <>
      <InputsWrapper numColumns={2}>
        <InputWrapper $useFormFieldTitles={useFormFieldTitles}>
          {getTitle(longLabel ?? t("labels.long_hedging"))}
          <CustomInputNumber
            data-testid={longInputTestId}
            value={longInputValue}
            onChange={updateLongHedging}
            precision={1}
            stringMode={true}
            placeholder={isMultiValue ? t("labels.multi") : undefined}
            suffix="%"
          />
        </InputWrapper>
        <InputWrapper $useFormFieldTitles={useFormFieldTitles}>
          {getTitle(shortLabel ?? t("labels.short_hedging"))}
          <CustomInputNumber
            data-testid={shortInputTestId}
            value={shortInputValue}
            onChange={updateShortHedging}
            precision={1}
            stringMode={true}
            placeholder={isMultiValue ? t("labels.multi") : undefined}
            suffix="%"
          />
        </InputWrapper>
      </InputsWrapper>
      <CompactSlider
        value={parsedLongHedgingRatio ?? 50}
        onChange={updateLongHedging}
        min={0}
        max={100}
        step={0.1}
        tooltip={{
          formatter: (value) => `${t("labels.long")}: ${value}%`,
        }}
      />
    </>
  );
};

export default HedgingRatioSelector;

const InputsWrapper = styled(EqualColumnsGrid)`
  margin-bottom: 4px;
`;
const InputWrapper = styled.div<{ $useFormFieldTitles?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$useFormFieldTitles ? 0 : 6)}px;
`;
const CompactSlider = styled(Slider)`
  &.ant-slider {
    height: 18px;
    margin: 12px 5px 0;
    padding: 4px 0;
  }
`;
